import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeT } from '../../../../group_leader/helpers/i18n'
import { to12hr, to24hr } from '../../../helpers/pairings'

// A typed "H:MM" field plus an AM/PM toggle over 24-hour "HH:MM"; commits only when it parses.

const t = makeT('group_leader.event_setup.pairings')
const PERIODS = [ 'AM', 'PM' ]

// "7:38 AM" -> { time: '7:38', period: 'AM' }; blank/invalid -> sensible defaults.
const splitParts = (value) => {
  const parts = (to12hr(value) || '').split(' ')
  return { time: parts[0] || '', period: parts[1] || 'AM' }
}

const AmPmTimePicker = ({ id, value, onChange }) => {
  const seed = splitParts(value)
  const [ draft, setDraft ] = useState(seed.time)
  const [ period, setPeriod ] = useState(seed.period)

  // Re-seed when the value changes from outside, and to normalise the display after a commit.
  useEffect(() => {
    const next = splitParts(value)
    setDraft(next.time)
    setPeriod(next.period)
  }, [ value ])

  // Combine the typed time with the chosen period and emit 24-hour when it parses.
  const emit = (time, chosenPeriod) => {
    const valid24 = to24hr(`${time.trim()} ${chosenPeriod}`)
    if (valid24) { onChange(valid24) }
    return !!valid24
  }

  const commitTime = () => {
    if (!emit(draft, period)) { setDraft(splitParts(value).time) }
  }

  const selectPeriod = (chosenPeriod) => {
    setPeriod(chosenPeriod)
    emit(draft, chosenPeriod) // apply immediately when the typed time is valid
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); commitTime() }
    if (event.key === 'Escape') { setDraft(splitParts(value).time) }
  }

  return (
    <div className="gl-event-setup-time-field">
      <input
        id={id}
        type="text"
        className="gl-event-setup-tt-cf-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitTime}
        onKeyDown={handleKeyDown}
        placeholder={t('tee_times.time_input_placeholder')}
      />
      <div className="gl-event-setup-seg-group" role="group" aria-label={t('tee_times.period_aria')}>
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            className={`gl-event-setup-seg-btn${period === p ? ' is-active' : ''}`}
            aria-pressed={period === p}
            onClick={() => selectPeriod(p)}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}

AmPmTimePicker.propTypes = {
  id: PropTypes.string,
  // 24-hour "HH:MM"
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

export default AmPmTimePicker
